import React, { useEffect, useMemo, useState } from "react";
import {
  Lock,
  LogOut,
  KeyRound,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Search as SearchIcon,
  MapPin,
  Calendar as CalendarIcon,
  Clock,
  Bike,
  ShieldCheck,
  Eye,
  EyeOff,
  Sun,
  Moon,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  empAvailability,
  empChangePassword,
  empLogin,
  empLogout,
  empMe,
} from "../lib/employeeApi";
import {
  SHOP_CLOSE,
  istDateInputStr,
  openingTime,
  toZohoDateTime,
} from "../lib/employeeDate";

// ------------------------------------------------------------------
// Shell
// ------------------------------------------------------------------
const EmployeePortal = () => {
  const [bootLoading, setBootLoading] = useState(true);
  const [authed, setAuthed] = useState(false);
  const [userId, setUserId] = useState("");

  useEffect(() => {
    document.title = "RR Bros · Employee Portal";
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "robots");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", "noindex,nofollow");

    (async () => {
      try {
        const me = await empMe();
        setUserId(me.user_id);
        setAuthed(true);
      } catch {
        setAuthed(false);
      } finally {
        setBootLoading(false);
      }
    })();
  }, []);

  const onLoggedIn = (uid) => {
    setUserId(uid);
    setAuthed(true);
  };

  const onLoggedOut = () => {
    setUserId("");
    setAuthed(false);
  };

  if (bootLoading) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
      </div>
    );
  }

  return authed ? (
    <Portal userId={userId} onLoggedOut={onLoggedOut} />
  ) : (
    <LoginView onLoggedIn={onLoggedIn} />
  );
};

export default EmployeePortal;

// ------------------------------------------------------------------
// Login view
// ------------------------------------------------------------------
const LoginView = ({ onLoggedIn }) => {
  const [uid, setUid] = useState("");
  const [pwd, setPwd] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!uid.trim() || !pwd) {
      setError("Please enter your user ID and password.");
      return;
    }
    setLoading(true);
    try {
      const data = await empLogin(uid.trim(), pwd);
      onLoggedIn(data.user_id);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Invalid user ID or password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100 antialiased flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-orange-500/10 border border-orange-500/30 mb-3">
            <ShieldCheck className="w-6 h-6 text-orange-400" />
          </div>
          <h1 className="text-2xl font-black tracking-tight">
            RR Bros · Employee Portal
          </h1>
          <p className="mt-1 text-xs text-neutral-400">
            Authorised staff only · Sign in to continue
          </p>
        </div>

        <form
          onSubmit={submit}
          noValidate
          data-testid="employee-login-form"
          className="bg-neutral-900 border border-neutral-800 rounded-2xl p-5 sm:p-6 space-y-4 shadow-2xl"
        >
          {error && (
            <div
              role="alert"
              data-testid="employee-login-error"
              className="flex items-start gap-2 bg-red-500/10 border border-red-500/30 text-red-300 rounded-xl px-3 py-2 text-sm"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-neutral-400 mb-1.5 block">
              User ID
            </label>
            <div className="relative">
              <KeyRound className="w-4 h-4 text-orange-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={uid}
                onChange={(e) => setUid(e.target.value)}
                placeholder="your user id"
                data-testid="employee-userid-input"
                className="w-full h-12 pl-10 pr-3 rounded-xl bg-neutral-950 border border-neutral-800 text-sm font-semibold text-neutral-100 outline-none focus:ring-2 focus:ring-orange-500/60"
              />
            </div>
          </div>

          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-neutral-400 mb-1.5 block">
              Password
            </label>
            <div className="relative">
              <Lock className="w-4 h-4 text-orange-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type={showPwd ? "text" : "password"}
                autoComplete="current-password"
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                placeholder="••••••••"
                data-testid="employee-password-input"
                className="w-full h-12 pl-10 pr-10 rounded-xl bg-neutral-950 border border-neutral-800 text-sm font-semibold text-neutral-100 outline-none focus:ring-2 focus:ring-orange-500/60"
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                aria-label={showPwd ? "Hide password" : "Show password"}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-neutral-400 hover:text-neutral-100"
              >
                {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="employee-login-submit"
            className="w-full h-12 rounded-xl bg-orange-500 hover:bg-orange-600 disabled:bg-neutral-700 text-white font-black text-sm uppercase tracking-wider inline-flex items-center justify-center gap-2 transition-colors"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Lock className="w-4 h-4" />
            )}
            Sign In
          </button>
        </form>

        <p className="text-center text-[11px] text-neutral-500 mt-5">
          This portal is private. If you shouldn’t be here, close the tab.
        </p>
      </div>
    </div>
  );
};

// ------------------------------------------------------------------
// Portal (authed)
// ------------------------------------------------------------------
const Portal = ({ userId, onLoggedOut }) => {
  const [showPwd, setShowPwd] = useState(false);

  const logout = async () => {
    try {
      await empLogout();
    } catch {
      /* ignore */
    }
    onLoggedOut();
  };

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900 antialiased">
      {/* Private top bar */}
      <header
        className="bg-neutral-900 text-neutral-100 border-b border-neutral-800 sticky top-0 z-20"
        data-testid="employee-topbar"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-orange-500/15 border border-orange-500/30 flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-4 h-4 text-orange-400" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-black tracking-[0.18em] text-orange-400 uppercase">
                RR Bros Bike Rental
              </p>
              <p className="text-sm font-black truncate">Employee Portal</p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <span
              data-testid="employee-user-label"
              className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-neutral-800 border border-neutral-700 text-[11px] font-bold uppercase tracking-wider text-neutral-200"
            >
              <KeyRound className="w-3.5 h-3.5 text-orange-400" />
              {userId}
            </span>
            <button
              type="button"
              onClick={() => setShowPwd(true)}
              data-testid="change-password-btn"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-neutral-800 hover:bg-neutral-700 text-[11px] font-bold uppercase tracking-wider text-neutral-200"
            >
              <KeyRound className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Change Password</span>
              <span className="sm:hidden">Password</span>
            </button>
            <button
              type="button"
              onClick={logout}
              data-testid="employee-logout-btn"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-red-500 hover:bg-red-600 text-[11px] font-bold uppercase tracking-wider text-white"
            >
              <LogOut className="w-3.5 h-3.5" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <AvailabilityPanel />

      <ChangePasswordDialog
        open={showPwd}
        onOpenChange={setShowPwd}
        onPasswordChanged={onLoggedOut}
      />
    </div>
  );
};

// ------------------------------------------------------------------
// Vehicle Availability search + results
// ------------------------------------------------------------------
const PICKUPS = ["Udupi", "Manipal"];

const timeOptions = () => {
  const out = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 15, 30, 45]) {
      const H = String(h).padStart(2, "0");
      const M = String(m).padStart(2, "0");
      out.push(`${H}:${M}`);
    }
  }
  return out;
};

const fmtTimeLabel = (hhmm) => {
  if (!hhmm) return "";
  const [H, M] = hhmm.split(":").map(Number);
  const period = H >= 12 ? "PM" : "AM";
  let h12 = H % 12;
  if (h12 === 0) h12 = 12;
  return `${String(h12).padStart(2, "0")}:${String(M).padStart(2, "0")} ${period}`;
};

const AvailabilityPanel = () => {
  const [pickup, setPickup] = useState("Udupi");
  const [startDate, setStartDate] = useState(istDateInputStr(0));
  const [startTime, setStartTime] = useState(openingTime("Udupi"));
  const [endDate, setEndDate] = useState(istDateInputStr(0));
  const [endTime, setEndTime] = useState(SHOP_CLOSE);
  const [quick, setQuick] = useState("TODAY");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [requestedAt, setRequestedAt] = useState("");

  const times = useMemo(() => timeOptions(), []);

  // Apply quick-option whenever user clicks a chip or changes pickup.
  const applyQuick = (q, pk = pickup) => {
    setQuick(q);
    if (q === "TODAY") {
      setStartDate(istDateInputStr(0));
      setEndDate(istDateInputStr(0));
      setStartTime(openingTime(pk));
      setEndTime(SHOP_CLOSE);
    } else if (q === "TOMORROW") {
      setStartDate(istDateInputStr(1));
      setEndDate(istDateInputStr(1));
      setStartTime(openingTime(pk));
      setEndTime(SHOP_CLOSE);
    }
    // CUSTOM: leave current values alone
  };

  const changePickup = (p) => {
    setPickup(p);
    if (quick !== "CUSTOM") applyQuick(quick, p);
  };

  const onCustomEdit = () => {
    if (quick !== "CUSTOM") setQuick("CUSTOM");
  };

  const run = async () => {
    setError("");
    setResult(null);
    if (!pickup) return setError("Please select a pickup point.");
    if (!startDate || !startTime || !endDate || !endTime)
      return setError("Please fill start and end date & time.");

    const start = toZohoDateTime(startDate, startTime);
    const end = toZohoDateTime(endDate, endTime);
    setRequestedAt(`${start} → ${end}`);

    setLoading(true);
    try {
      const data = await empAvailability(pickup, start, end);
      setResult(data);
      if (!data.success)
        setError(data.message || "No availability returned by Zoho.");
    } catch (err) {
      if (err?.response?.status === 401) {
        setError("Your session has expired. Please sign in again.");
        setTimeout(() => window.location.reload(), 900);
      } else {
        const d = err?.response?.data?.detail;
        setError(typeof d === "string" ? d : "Failed to fetch availability.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
      <div className="mb-5 sm:mb-7">
        <p className="text-[11px] font-black uppercase tracking-[0.22em] text-orange-500">
          Main Feature
        </p>
        <h1 className="text-2xl sm:text-3xl font-black mt-1 flex items-center gap-2">
          Vehicle Availability
        </h1>
        <p className="text-sm text-neutral-500 mt-1">
          Live data from Zoho Creator · <strong>Get_Website_Availability</strong>
        </p>
      </div>

      <section
        data-testid="availability-form"
        className="bg-white border border-neutral-200 rounded-2xl shadow-sm p-4 sm:p-6"
      >
        <p className="text-[11px] font-black uppercase tracking-[0.18em] text-neutral-500 mb-3">
          Check Vehicle Availability
        </p>

        {/* Quick chips */}
        <div className="flex items-center gap-2 mb-5">
          {[
            { key: "TODAY", label: "Today", Icon: Sun },
            { key: "TOMORROW", label: "Tomorrow", Icon: Moon },
            { key: "CUSTOM", label: "Custom", Icon: CalendarIcon },
          ].map(({ key, label, Icon }) => {
            const active = quick === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => applyQuick(key)}
                data-testid={`quick-${key.toLowerCase()}`}
                className={`inline-flex items-center gap-1.5 h-9 px-3 sm:px-4 rounded-full text-[11px] font-black uppercase tracking-wider transition-colors border ${
                  active
                    ? "bg-neutral-900 text-white border-neutral-900"
                    : "bg-white text-neutral-700 border-neutral-200 hover:border-neutral-300"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 sm:gap-4">
          {/* Pickup */}
          <div className="md:col-span-1">
            <Label icon={MapPin}>Pickup Point</Label>
            <select
              value={pickup}
              onChange={(e) => changePickup(e.target.value)}
              data-testid="field-pickup"
              className="w-full h-12 px-3 rounded-xl bg-neutral-50 border border-neutral-200 text-sm font-semibold text-neutral-900 outline-none focus:ring-2 focus:ring-orange-500/40"
            >
              {PICKUPS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>

          {/* Start */}
          <div className="md:col-span-1">
            <Label icon={CalendarIcon}>Start Date</Label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                onCustomEdit();
              }}
              data-testid="field-start-date"
              className="w-full h-12 px-3 rounded-xl bg-neutral-50 border border-neutral-200 text-sm font-semibold text-neutral-900 outline-none focus:ring-2 focus:ring-orange-500/40"
            />
          </div>
          <div className="md:col-span-1">
            <Label icon={Clock}>Start Time</Label>
            <select
              value={startTime}
              onChange={(e) => {
                setStartTime(e.target.value);
                onCustomEdit();
              }}
              data-testid="field-start-time"
              className="w-full h-12 px-3 rounded-xl bg-neutral-50 border border-neutral-200 text-sm font-semibold text-neutral-900 outline-none focus:ring-2 focus:ring-orange-500/40"
            >
              {times.map((t) => (
                <option key={t} value={t}>
                  {fmtTimeLabel(t)}
                </option>
              ))}
            </select>
          </div>

          {/* End */}
          <div className="md:col-span-1">
            <Label icon={CalendarIcon}>End Date</Label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                onCustomEdit();
              }}
              data-testid="field-end-date"
              className="w-full h-12 px-3 rounded-xl bg-neutral-50 border border-neutral-200 text-sm font-semibold text-neutral-900 outline-none focus:ring-2 focus:ring-orange-500/40"
            />
          </div>
          <div className="md:col-span-1">
            <Label icon={Clock}>End Time</Label>
            <select
              value={endTime}
              onChange={(e) => {
                setEndTime(e.target.value);
                onCustomEdit();
              }}
              data-testid="field-end-time"
              className="w-full h-12 px-3 rounded-xl bg-neutral-50 border border-neutral-200 text-sm font-semibold text-neutral-900 outline-none focus:ring-2 focus:ring-orange-500/40"
            >
              {times.map((t) => (
                <option key={t} value={t}>
                  {fmtTimeLabel(t)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <button
          type="button"
          onClick={run}
          disabled={loading}
          data-testid="check-availability-btn"
          className="mt-5 w-full md:w-auto md:min-w-[260px] h-14 px-6 rounded-xl bg-orange-500 hover:bg-orange-600 disabled:bg-neutral-300 disabled:text-neutral-500 text-white font-black text-sm uppercase tracking-wider inline-flex items-center justify-center gap-2 shadow-md transition-colors"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <SearchIcon className="w-4 h-4" />
          )}
          {loading ? "Checking…" : "Check Availability"}
        </button>
      </section>

      {/* Results */}
      <section className="mt-6 sm:mt-8" data-testid="availability-results">
        {loading && (
          <div
            data-testid="loading-indicator"
            className="rounded-2xl border border-dashed border-neutral-300 bg-white p-10 flex flex-col items-center gap-3"
          >
            <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
            <p className="text-sm text-neutral-500 font-semibold">
              Fetching live availability from Zoho…
            </p>
          </div>
        )}

        {!loading && error && (
          <div
            data-testid="availability-error"
            className="rounded-2xl border border-red-200 bg-red-50 p-5 flex items-start gap-3"
          >
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-black text-red-800">
                Could not fetch availability
              </p>
              <p className="text-sm text-red-700 mt-0.5">{error}</p>
            </div>
          </div>
        )}

        {!loading && !error && result && result.success && (
          <>
            <AvailabilitySummary vehicles={result.vehicles} />

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-neutral-500 mb-4">
              <span>
                <strong className="text-neutral-900">{result.pickup_point}</strong>
              </span>
              <span>·</span>
              <span>{result.start_datetime}</span>
              <span>→</span>
              <span>{result.end_datetime}</span>
              {result.duration && (
                <>
                  <span>·</span>
                  <span>{result.duration}</span>
                </>
              )}
              {result.pricing_category && (
                <>
                  <span>·</span>
                  <span className="inline-flex items-center h-6 px-2 rounded-full bg-neutral-900 text-white text-[10px] font-black uppercase tracking-wider">
                    {result.pricing_category}
                  </span>
                </>
              )}
            </div>

            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
              data-testid="results-grid"
            >
              {result.vehicles.map((v, i) => (
                <VehicleCard key={`${v.model || i}-${i}`} v={v} index={i} />
              ))}
              {result.vehicles.length === 0 && (
                <div className="col-span-full rounded-2xl border border-neutral-200 bg-white p-6 text-center text-sm text-neutral-500">
                  Zoho returned no vehicles for this slot.
                </div>
              )}
            </div>
          </>
        )}

        {!loading && !error && !result && (
          <p className="text-sm text-neutral-500 text-center py-8">
            Pick a slot and tap <strong>Check Availability</strong> to see live
            counts from Zoho.
          </p>
        )}
      </section>
    </main>
  );
};

const Label = ({ icon: Icon, children }) => (
  <label className="text-[10px] font-black uppercase tracking-[0.14em] text-neutral-500 mb-1 flex items-center gap-1">
    {Icon && <Icon className="w-3 h-3 text-orange-500" />}
    {children}
  </label>
);

// ------------------------------------------------------------------
// Totals banner above the per-model cards.
//   ONLINE WEBSITE AVAILABILITY = Σ available_quantity   (what customers see)
//   PHYSICALLY AVAILABLE        = Σ current_available    (what's really at the pickup)
// Both sums are over the vehicles Zoho returned for this search.
// ------------------------------------------------------------------
const sumField = (vehicles, key) =>
  (vehicles || []).reduce((acc, v) => acc + (Number(v?.[key]) || 0), 0);

const AvailabilitySummary = ({ vehicles }) => {
  const onlineTotal = sumField(vehicles, "available_quantity");
  const physicalTotal = sumField(vehicles, "current_available");
  return (
    <section
      data-testid="availability-summary"
      className="mb-5 sm:mb-6 rounded-2xl overflow-hidden border border-neutral-900"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-neutral-800">
        {/* Primary: online website */}
        <div className="bg-neutral-900 text-neutral-100 p-5 sm:p-6">
          <p className="text-[10px] font-black uppercase tracking-[0.22em] text-orange-400">
            Online Website Availability
          </p>
          <div className="mt-2 flex items-baseline gap-2">
            <span
              data-testid="summary-online-total"
              className="text-5xl sm:text-6xl font-black leading-none"
            >
              {onlineTotal}
            </span>
            <span className="text-xs font-black uppercase tracking-wider text-neutral-400">
              Scooters
            </span>
          </div>
          <p className="text-[11px] text-neutral-400 mt-2 leading-snug">
            Total currently being offered on the public website for this slot.
          </p>
        </div>
        {/* Secondary: physical */}
        <div className="bg-white p-5 sm:p-6">
          <p className="text-[10px] font-black uppercase tracking-[0.22em] text-neutral-500">
            Physically Available
          </p>
          <div className="mt-2 flex items-baseline gap-2">
            <span
              data-testid="summary-physical-total"
              className="text-5xl sm:text-6xl font-black leading-none text-neutral-900"
            >
              {physicalTotal}
            </span>
            <span className="text-xs font-black uppercase tracking-wider text-neutral-500">
              Scooters
            </span>
          </div>
          <p className="text-[11px] text-neutral-500 mt-2 leading-snug">
            Actual scooters we have at the pickup point before the online
            safety reserve.
          </p>
        </div>
      </div>
    </section>
  );
};

// ------------------------------------------------------------------
// Vehicle card — two headline numbers (physical + online website), then
// a compact detail list for Safety Reserve / Expected Returns /
// Expected Returns Online / Committed Bookings / Price / Duration.
// No "Current Available" label anywhere.
// ------------------------------------------------------------------
const VehicleCard = ({ v, index }) => {
  const physical =
    v.current_available == null ? null : Number(v.current_available);
  const online =
    v.available_quantity == null ? null : Number(v.available_quantity);
  const outOfStock = (online ?? 0) <= 0;
  const price = v.price != null ? Number(v.price) : null;
  const money = (n) =>
    typeof n === "number"
      ? `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`
      : "—";

  const rows = [
    ["Safety Reserve", v.safety_reserve],
    ["Expected Returns", v.expected_returns],
    ["Expected Returns Online", v.expected_returns_online],
    ["Committed Bookings", v.committed_bookings],
  ].filter(([, val]) => val !== undefined && val !== null && val !== "");

  return (
    <article
      data-testid={`vehicle-card-${index}`}
      className={`rounded-2xl border bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow ${
        outOfStock ? "border-red-200" : "border-neutral-200"
      }`}
    >
      <div className="p-5">
        {/* Header: model */}
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center flex-shrink-0">
            <Bike className="w-5 h-5 text-orange-500" />
          </div>
          <div className="min-w-0 flex-1">
            <p
              data-testid={`vehicle-model-${index}`}
              className="text-sm sm:text-base font-black text-neutral-900 truncate"
              title={v.model}
            >
              {v.model || "Unknown model"}
            </p>
            <p className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mt-0.5">
              Live from Zoho
            </p>
          </div>
        </div>

        {/* Two headline numbers */}
        <div className="mt-5 grid grid-cols-2 gap-3">
          <div className="rounded-xl bg-neutral-50 border border-neutral-200 p-3">
            <p className="text-[9px] font-black uppercase tracking-[0.18em] text-neutral-500">
              Physically Available
            </p>
            <p
              data-testid={`vehicle-physical-${index}`}
              className="mt-1 text-3xl sm:text-4xl font-black leading-none text-neutral-900"
            >
              {physical ?? "—"}
            </p>
          </div>
          <div
            className={`rounded-xl p-3 border ${
              outOfStock
                ? "bg-red-50 border-red-200"
                : "bg-orange-500 border-orange-500 text-white"
            }`}
          >
            <p
              className={`text-[9px] font-black uppercase tracking-[0.18em] ${
                outOfStock ? "text-red-500" : "text-white/80"
              }`}
            >
              Online Website
            </p>
            <p
              data-testid={`vehicle-online-${index}`}
              className={`mt-1 text-3xl sm:text-4xl font-black leading-none ${
                outOfStock ? "text-red-500" : "text-white"
              }`}
            >
              {online ?? "—"}
            </p>
            {outOfStock && (
              <p className="mt-1 text-[9px] font-black uppercase tracking-wider text-red-500">
                Not Available
              </p>
            )}
          </div>
        </div>

        {/* Detail rows */}
        {(rows.length > 0 || price != null || v.duration) && (
          <dl className="mt-4 pt-4 border-t border-neutral-100 grid grid-cols-2 gap-x-3 gap-y-2 text-[11px]">
            {rows.map(([label, val]) => (
              <div key={label} className="col-span-2 flex items-center justify-between">
                <dt className="text-neutral-500">{label}</dt>
                <dd className="font-black text-neutral-900">{val}</dd>
              </div>
            ))}
            {price != null && (
              <div className="col-span-2 flex items-center justify-between">
                <dt className="text-neutral-500">Rental Price</dt>
                <dd className="font-black text-orange-600">{money(price)}</dd>
              </div>
            )}
            {v.duration && (
              <div className="col-span-2 flex items-center justify-between">
                <dt className="text-neutral-500">Rental Duration</dt>
                <dd className="font-black text-neutral-900">{v.duration}</dd>
              </div>
            )}
            {v.pricing_type && (
              <div className="col-span-2 flex items-center justify-between">
                <dt className="text-neutral-500">Pricing</dt>
                <dd className="font-black text-neutral-900">{v.pricing_type}</dd>
              </div>
            )}
          </dl>
        )}
      </div>
    </article>
  );
};

// ------------------------------------------------------------------
// Change-password dialog
// ------------------------------------------------------------------
const ChangePasswordDialog = ({ open, onOpenChange, onPasswordChanged }) => {
  const [cur, setCur] = useState("");
  const [np, setNp] = useState("");
  const [cp, setCp] = useState("");
  const [showCur, setShowCur] = useState(false);
  const [showNp, setShowNp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (!open) {
      setCur("");
      setNp("");
      setCp("");
      setError("");
      setSuccess("");
      setLoading(false);
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!cur || !np || !cp) return setError("All fields are required.");
    if (np.length < 8)
      return setError("New password must be at least 8 characters.");
    if (np !== cp) return setError("New password and confirmation do not match.");
    if (np === cur)
      return setError("New password must be different from the current password.");

    setLoading(true);
    try {
      await empChangePassword(cur, np, cp);
      setSuccess(
        "Password updated. For security you need to sign in again with the new password."
      );
      setTimeout(() => {
        onOpenChange(false);
        onPasswordChanged();
      }, 1200);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Could not change password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle>Change Password</DialogTitle>
          <DialogDescription>
            After changing your password you’ll be signed out and asked to log
            in again with the new one.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} noValidate className="space-y-3">
          {error && (
            <div
              role="alert"
              data-testid="change-pwd-error"
              className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2 text-sm"
            >
              <AlertCircle className="w-4 h-4 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
          {success && (
            <div
              role="status"
              data-testid="change-pwd-success"
              className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl px-3 py-2 text-sm"
            >
              <CheckCircle2 className="w-4 h-4 mt-0.5" />
              <span>{success}</span>
            </div>
          )}

          <PwdInput
            label="Current Password"
            value={cur}
            onChange={setCur}
            show={showCur}
            onToggle={() => setShowCur((v) => !v)}
            testid="current-password-input"
          />
          <PwdInput
            label="New Password"
            value={np}
            onChange={setNp}
            show={showNp}
            onToggle={() => setShowNp((v) => !v)}
            testid="new-password-input"
          />
          <PwdInput
            label="Confirm New Password"
            value={cp}
            onChange={setCp}
            show={showNp}
            onToggle={() => setShowNp((v) => !v)}
            testid="confirm-password-input"
          />

          <DialogFooter className="pt-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="h-11 px-4 rounded-xl bg-neutral-100 hover:bg-neutral-200 text-sm font-bold text-neutral-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              data-testid="change-password-submit"
              className="h-11 px-4 rounded-xl bg-orange-500 hover:bg-orange-600 disabled:bg-neutral-300 text-white font-black text-sm uppercase tracking-wider inline-flex items-center justify-center gap-2"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <KeyRound className="w-4 h-4" />
              )}
              Update Password
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const PwdInput = ({ label, value, onChange, show, onToggle, testid }) => (
  <div>
    <label className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1 block">
      {label}
    </label>
    <div className="relative">
      <Lock className="w-4 h-4 text-orange-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full h-12 pl-10 pr-10 rounded-xl bg-neutral-50 border border-neutral-200 text-sm font-semibold text-neutral-900 outline-none focus:ring-2 focus:ring-orange-500/40"
      />
      <button
        type="button"
        onClick={onToggle}
        aria-label={show ? "Hide password" : "Show password"}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-neutral-400 hover:text-neutral-800"
      >
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  </div>
);
